import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowLeft, Users, Plus, User, Briefcase, DollarSign,
  Edit3, Trash2, Save, X, Loader2, CheckCircle2, AlertCircle,
  BadgeCheck, BadgeX, Filter,
} from 'lucide-react';
import { Haptics } from '../../utils/haptics';
import { confirmAction } from './ConfirmModal';

interface Employee {
  id: number;
  name: string;
  position: string;
  basicSalary: number;
  status: 'Active' | 'Inactive';
  createdAt: string;
}

interface EmployeeManagementProps {
  storeApiKey: string;
  onBack: () => void;
  inline?: boolean;
}

const SALARY_PRESETS = ['Monthly', 'Part-Time', 'Per Shift'];
const POSITIONS = ['Cashier', 'Manager', 'Inventory Clerk', 'Sales Associate', 'Admin', 'Cleaner', 'Security', 'Other'];

const EmployeeManagement: React.FC<EmployeeManagementProps> = ({ storeApiKey, onBack, inline }) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [statusFilter, setStatusFilter] = useState<'All' | 'Active' | 'Inactive'>('All');

  const [form, setForm] = useState({
    name: '',
    position: 'Cashier',
    basicSalary: '',
    status: 'Active' as 'Active' | 'Inactive',
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/employees', {
        headers: { 'x-store-api-key': storeApiKey },
      });
      const data = await res.json();
      if (res.ok) setEmployees(data.employees || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const resetForm = () => {
    setForm({ name: '', position: 'Cashier', basicSalary: '', status: 'Active' });
    setFormError('');
  };

  const createEmployee = async () => {
    setFormError('');
    if (!form.name.trim()) {
      setFormError('Employee name is required');
      Haptics.fire('warn');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch('/api/employees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey },
        body: JSON.stringify({
          name: form.name.trim(),
          position: form.position,
          basicSalary: Number(form.basicSalary) || 0,
          status: form.status,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.error || 'Failed to create'); Haptics.fire('warn'); return; }
      Haptics.fire('success');
      setCreateOpen(false);
      resetForm();
      fetchEmployees();
    } catch { setFormError('Network error'); Haptics.fire('warn'); }
    finally { setSaving(false); }
  };

  const updateEmployee = async () => {
    if (!editing) return;
    setFormError('');
    setSaving(true);
    try {
      const body: any = {};
      if (form.name !== undefined) body.name = form.name.trim();
      if (form.position !== undefined) body.position = form.position;
      if (form.basicSalary !== undefined) body.basicSalary = Number(form.basicSalary) || 0;
      if (form.status !== undefined) body.status = form.status;
      const res = await fetch(`/api/employees/${editing.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.error || 'Failed to update'); Haptics.fire('warn'); return; }
      Haptics.fire('success');
      setEditOpen(false);
      setEditing(null);
      resetForm();
      fetchEmployees();
    } catch { setFormError('Network error'); Haptics.fire('warn'); }
    finally { setSaving(false); }
  };

  const deleteEmployee = async (e: Employee) => {
    const ok = await confirmAction({
      title: `Remove ${e.name}?`,
      message: `This will permanently delete ${e.name} from employee records.`,
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await fetch(`/api/employees/${e.id}`, {
        method: 'DELETE',
        headers: { 'x-store-api-key': storeApiKey },
      });
      if (res.ok) { Haptics.fire('warn'); fetchEmployees(); }
    } catch { /* silent */ }
  };

  const beginEdit = (e: Employee) => {
    setEditing(e);
    setForm({
      name: e.name,
      position: e.position,
      basicSalary: String(e.basicSalary),
      status: e.status,
    });
    setFormError('');
    setEditOpen(true);
  };

  const filtered = statusFilter === 'All'
    ? employees
    : employees.filter(e => e.status === statusFilter);

  return (
    <div className={inline ? 'flex flex-col h-full bg-gradient-to-br from-slate-50 via-white to-slate-100' : 'fixed inset-0 z-[60] bg-gradient-to-br from-slate-50 via-white to-slate-100 flex flex-col'}>
      <div className="shrink-0 flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="flex items-center gap-2">
          {!inline && <button onClick={onBack}
            className="w-9 h-9 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 transition-colors">
            <ArrowLeft size={15} />
          </button>}
          <div>
            <h2 className="text-[15px] font-black text-slate-800 flex items-center gap-2">
              <Users size={15} className="text-emerald-500" /> Employee Management
            </h2>
            <p className="text-[10px] text-slate-500">Staff positions & base salaries</p>
          </div>
        </div>
        <motion.button whileTap={{ scale: 0.96 }}
          onClick={() => { setCreateOpen(true); resetForm(); }}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-600 text-white text-[11px] font-black uppercase tracking-wider shadow-lg shadow-emerald-500/20">
          <Plus size={13} /> Add Employee
        </motion.button>
      </div>

      <div className="shrink-0 flex items-center gap-2 px-5 py-2 border-b border-slate-100 bg-white/40 backdrop-blur-sm">
        {(['All', 'Active', 'Inactive'] as const).map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${
              statusFilter === s
                ? 'bg-emerald-100 text-emerald-700 border border-emerald-200 shadow-sm'
                : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
            }`}>
            {s === 'All' ? <Filter size={10} className="inline mr-1" /> : null}
            {s}
          </button>
        ))}
        <div className="flex-1 text-right text-[10px] text-slate-400 font-mono">{filtered.length} employee{filtered.length !== 1 ? 's' : ''}</div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {loading && (
          <div className="flex items-center justify-center py-12 text-slate-400 text-[11px]">
            <Loader2 size={14} className="animate-spin mr-2" /> Loading…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-12 text-slate-400 text-[11px]">
            <Users size={28} className="mx-auto mb-2 text-slate-300" />
            {statusFilter !== 'All' ? 'No employees match this filter.' : 'No employees yet. Add your first team member.'}
          </div>
        )}
        {filtered.map((e) => (
          <motion.div key={e.id} layout
            className={`rounded-2xl border p-3 bg-white/70 backdrop-blur-sm transition-all ${
              e.status === 'Active' ? 'border-slate-200' : 'border-slate-200/60 opacity-70'
            }`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${
                e.status === 'Active' ? 'from-emerald-400 to-teal-500' : 'from-slate-300 to-slate-400'
              } flex items-center justify-center text-white shrink-0 shadow-sm`}>
                <User size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[12px] font-black text-slate-800 truncate">{e.name}</span>
                  {e.status === 'Active'
                    ? <BadgeCheck size={11} className="text-emerald-500 shrink-0" />
                    : <BadgeX size={11} className="text-slate-400 shrink-0" />}
                </div>
                <div className="text-[10px] text-slate-500 font-mono flex items-center gap-2 mt-0.5">
                  <Briefcase size={9} className="text-slate-400" />
                  <span>{e.position}</span>
                  <span>·</span>
                  <DollarSign size={9} className="text-slate-400" />
                  <span>Rs. {Number(e.basicSalary).toLocaleString()}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => beginEdit(e)}
                  className="w-8 h-8 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 transition-colors"
                  title="Edit">
                  <Edit3 size={12} />
                </button>
                <button onClick={() => deleteEmployee(e)}
                  className="w-8 h-8 rounded-lg bg-rose-50 hover:bg-rose-100 border border-rose-200 flex items-center justify-center text-rose-500 transition-colors"
                  title="Remove">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {createOpen && (
          <Modal onClose={() => setCreateOpen(false)} title="Add Employee">
            <div className="space-y-3 p-1">
              <Field label="Full Name" icon={User}>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Kamal Perera"
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-emerald-400 text-[13px]" />
              </Field>
              <Field label="Position" icon={Briefcase}>
                <select value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-emerald-400 text-[13px]">
                  {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Monthly Salary (Rs.)" icon={DollarSign}>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[13px] font-mono">Rs.</span>
                  <input type="number" min={0} step={100} value={form.basicSalary}
                    onChange={e => setForm(f => ({ ...f, basicSalary: e.target.value }))}
                    placeholder="0.00"
                    className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-emerald-400 text-[13px] font-mono" />
                </div>
              </Field>
              <div className="flex gap-1.5 flex-wrap">
                {SALARY_PRESETS.map(p => (
                  <button key={p} type="button" onClick={() => setForm(f => ({ ...f, position: p }))}
                    className="px-2.5 py-1 rounded-lg bg-teal-50 border border-teal-200 text-teal-700 text-[9px] font-bold uppercase tracking-wider hover:bg-teal-100 transition-colors">
                    {p}
                  </button>
                ))}
              </div>
              {formError && (
                <div className="text-[11px] font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                  {formError}
                </div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <button onClick={() => setCreateOpen(false)}
                  className="flex-1 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 text-[11px] font-black uppercase">
                  Cancel
                </button>
                <motion.button whileTap={{ scale: 0.97 }} onClick={createEmployee} disabled={saving}
                  className="flex-1 py-2.5 rounded-xl bg-emerald-600 text-white text-[11px] font-black uppercase shadow-lg shadow-emerald-500/20 disabled:opacity-50 flex items-center justify-center gap-1">
                  {saving ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                  Create
                </motion.button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {editOpen && editing && (
          <Modal onClose={() => setEditOpen(false)} title="Edit Employee">
            <div className="space-y-3 p-1">
              <Field label="Full Name" icon={User}>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-emerald-400 text-[13px]" />
              </Field>
              <Field label="Position" icon={Briefcase}>
                <select value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-emerald-400 text-[13px]">
                  {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Monthly Salary (Rs.)" icon={DollarSign}>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[13px] font-mono">Rs.</span>
                  <input type="number" min={0} step={100} value={form.basicSalary}
                    onChange={e => setForm(f => ({ ...f, basicSalary: e.target.value }))}
                    className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-emerald-400 text-[13px] font-mono" />
                </div>
              </Field>
              <div className="flex items-center gap-2 py-1">
                <button onClick={() => setForm(f => ({ ...f, status: f.status === 'Active' ? 'Inactive' : 'Active' }))}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl border text-[11px] font-black uppercase transition-all ${
                    form.status === 'Active'
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-600'
                      : 'bg-slate-50 border-slate-200 text-slate-500'
                  }`}>
                  {form.status === 'Active' ? <CheckCircle2 size={12} /> : <BadgeX size={12} />}
                  {form.status === 'Active' ? 'Active' : 'Inactive'}
                </button>
              </div>
              {formError && (
                <div className="text-[11px] font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                  {formError}
                </div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <button onClick={() => setEditOpen(false)}
                  className="flex-1 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 text-[11px] font-black uppercase">
                  Cancel
                </button>
                <motion.button whileTap={{ scale: 0.97 }} onClick={updateEmployee} disabled={saving}
                  className="flex-1 py-2.5 rounded-xl bg-emerald-600 text-white text-[11px] font-black uppercase shadow-lg shadow-emerald-500/20 disabled:opacity-50 flex items-center justify-center gap-1">
                  {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                  Save
                </motion.button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
};

const Field: React.FC<{ label: string; icon: any; children: React.ReactNode }> = ({ label, icon: Icon, children }) => (
  <div className="space-y-1">
    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
      <Icon size={10} /> {label}
    </label>
    {children}
  </div>
);

const Modal: React.FC<{ onClose: () => void; title: string; children: React.ReactNode }> = ({ onClose, title, children }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-[9998] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <motion.div initial={{ y: 400 }} animate={{ y: 0 }} exit={{ y: 400 }}
      transition={{ type: 'spring', stiffness: 380, damping: 32 }}
      className="w-full sm:max-w-md max-h-[92vh] bg-white/90 backdrop-blur-2xl rounded-t-3xl sm:rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col">
      <div className="shrink-0 px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-[14px] font-black text-slate-800">{title}</h3>
        <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700">
          <X size={15} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">{children}</div>
    </motion.div>
  </motion.div>
);

export default EmployeeManagement;
